import type { ApiLogItem } from '../api/types';

/** Bootstrap colour of an HTTP status: green 2xx, amber 4xx, red 5xx or no response. */
export function statusClass(status: number): string {
  if (status === 0 || status >= 500) return 'bg-danger-subtle text-danger border border-danger-subtle';
  if (status >= 400) return 'bg-warning-subtle text-warning border border-warning-subtle';
  return 'bg-success-subtle text-success border border-success-subtle';
}

/** The status badge and error code; a webhook that got no response shows "no response". */
export function ApiLogStatus({ log }: { log: Pick<ApiLogItem, 'statusCode' | 'errorCode'> }) {
  return (
    <>
      <span className={`badge badge-status ${statusClass(log.statusCode)}`}>{log.statusCode === 0 ? 'no response' : log.statusCode}</span>
      {log.errorCode && <small className="text-body-secondary ms-1">{log.errorCode}</small>}
    </>
  );
}

/**
 * A webhook event as an outline badge: a decision in the success colour, a changed decision in the
 * danger colour so it stands out in a timeline (site.css .badge-outline).
 */
export function WebhookEventBadge({ event }: { event: string | null }) {
  const theme = event === 'verification.decision_changed' ? 'theme-danger' : 'theme-success';
  return <span className={`badge badge-outline ${theme} me-1`}>{event ?? 'webhook'}</span>;
}

/** "POST /v1/…" for a call; "[Webhook] [verification.decided] → https://…" for a delivery. */
export function ApiLogRequest({ log }: { log: Pick<ApiLogItem, 'kind' | 'event' | 'method' | 'path'> }) {
  if (log.kind === 'webhook') {
    return (
      <>
        <span className="badge badge-outline theme-inverse me-1">Webhook</span>
        <WebhookEventBadge event={log.event} />
        <code className="small">→ {log.path}</code>
      </>
    );
  }
  return (
    <>
      <span className="fw-medium">{log.method}</span> <code className="small">{log.path}</code>
    </>
  );
}
